import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VerifiedBadge from "../components/VerifiedBadge";
import { Send, ShieldAlert, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Chat() {
  const { partnerId } = useParams();
  const { user } = useAuth();
  const [partner, setPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      setViewportHeight(vv.height);
      // Re-anchor scroll to bottom when keyboard opens/closes, if user was near bottom
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
          el.scrollTo({ top: el.scrollHeight });
        }
      });
    };
    vv.addEventListener("resize", onResize);
    onResize();
    return () => vv.removeEventListener("resize", onResize);
  }, []);

  const load = async () => {
    const { data } = await api.get(`/conversations/${partnerId}`);
    setPartner(data.partner);
    setMessages(prev => {
      if (prev.length === data.messages.length && prev.length > 0 &&
          prev[prev.length - 1].id === data.messages[data.messages.length - 1].id) {
        return prev; // no new messages, keep same reference to skip re-render/scroll
      }
      return data.messages;
    });
    api.post(`/conversations/${partnerId}/read`).catch(() => {});
  };
  const isNearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [partnerId]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const wasNearBottom = isNearBottom();
    if (wasNearBottom) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  // Poll every 6s for new messages
  useEffect(() => {
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [partnerId]);

  const send = async (e) => {
    e.preventDefault();
    const content = text.trim();
    if (!content) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { to: partnerId, content });
      setMessages(m => [...m, data]);
      setText("");
      requestAnimationFrame(() => {
        const el = document.querySelector('[data-testid="chat-input"]');
        if (el) el.style.height = "auto";
      });
      if (data.redacted) toast.warning("Some details were auto-hidden to keep chats on EarnMate.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (!partner) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;

  const dayLabel = (d) => {
    const date = new Date(d);
    const today = new Date();
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const sameDay = (a, b) => a.toDateString() === b.toDateString();
    if (sameDay(date, today)) return "Today";
    if (sameDay(date, yesterday)) return "Yesterday";
    return date.toLocaleDateString([], { day: "numeric", month: "short", year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined });
  };
  const GROUP_GAP_MS = 5 * 60 * 1000; // 5 minutes
  const rows = [];
  messages.forEach((m, i) => {
    const prev = messages[i - 1];
    const next = messages[i + 1];
    const isNewDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
    if (isNewDay) rows.push({ type: "divider", key: `div-${m.id}`, label: dayLabel(m.created_at) });
    const groupedWithPrev = !isNewDay && prev && prev.from_user === m.from_user &&
      (new Date(m.created_at) - new Date(prev.created_at)) < GROUP_GAP_MS;
    const isNewDayNext = !next || new Date(m.created_at).toDateString() !== new Date(next.created_at).toDateString();
    const groupedWithNext = !isNewDayNext && next && next.from_user === m.from_user &&
      (new Date(next.created_at) - new Date(m.created_at)) < GROUP_GAP_MS;
    rows.push({ type: "message", key: m.id, message: m, grouped: groupedWithPrev, isLastInGroup: !groupedWithNext });
  });

  return (
    <div
      className="mx-auto max-w-3xl flex flex-col"
      style={{ height: viewportHeight ? `${viewportHeight}px` : "calc(100vh - 4rem)" }}
      data-testid="chat-page"
    >
      <div className="px-4 sm:px-6 py-3 border-b border-border flex items-center gap-3 bg-card">
        <Link to="/messages" className="icon-btn !w-9 !h-9" data-testid="chat-back"><ArrowLeft className="w-4 h-4" /></Link>
        {partner.avatar
          ? <img src={partner.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
          : <div className="w-10 h-10 rounded-full bg-brand-tint text-brand grid place-items-center font-display font-black">{(partner.name||"?")[0]}</div>}
        <div className="min-w-0">
          <Link to={`/profile/${partner.username}`} className="font-medium flex items-center gap-1 hover:underline">
            {partner.name} {partner.verified && <VerifiedBadge size={14} />}
          </Link>
          <p className="text-xs text-muted-foreground capitalize">{partner.role}</p>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-2 bg-brand-tint/40 border-b border-border flex items-center gap-2 text-xs text-foreground/80">
        <ShieldAlert className="w-3.5 h-3.5 text-brand shrink-0" />
        <span>For your safety, phone numbers, emails, and external links are auto-hidden. Always keep payments and chats on EarnMate.</span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 bg-background">
          {messages.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-12">No messages yet. Say hi 👋</p>
          )}
          {rows.map(row => {
            if (row.type === "divider") {
              return (
                <div key={row.key} className="flex items-center justify-center my-4">
                  <span className="text-[11px] font-medium text-muted-foreground bg-muted px-3 py-1 rounded-full">{row.label}</span>
                </div>
              );
            }
            const m = row.message;
            const mine = m.from_user === user?.id;
            return (
              <div
                key={row.key}
                className={`flex ${mine ? "justify-end" : "justify-start"} ${row.grouped ? "mt-0.5" : "mt-3"}`}
                data-testid={`msg-${m.id}`}
              >
                <div className={`max-w-[80%] px-4 py-2.5 text-sm rounded-2xl ${mine
                    ? `bg-brand text-brand-fg ${row.isLastInGroup ? "rounded-br-md" : "rounded-br-2xl"}`
                    : `bg-muted text-foreground ${row.isLastInGroup ? "rounded-bl-md" : "rounded-bl-2xl"}`
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{m.content}</p>
                  <p className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</p>
                </div>
              </div>
            );
          })}
        </div>

      <form onSubmit={send} className="px-3 sm:px-6 py-3 border-t border-border bg-card flex items-end gap-2" data-testid="chat-form">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(e);
            }
          }}
          placeholder="Type a message…"
          rows={1}
          className="input-base flex-1 resize-none max-h-32 overflow-y-auto py-2.5"
          style={{ height: "auto" }}
          onInput={e => {
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 128) + "px";
          }}
          data-testid="chat-input"
        />
        <button disabled={sending || !text.trim()} className="btn-primary !px-4 !py-2.5 shrink-0" data-testid="chat-send">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
